// Vehicle drawer — Brand → Model → Year cascade in the header "Select Your
// Vehicle" overlay, using custom searchable dropdowns (not native <select>) so a
// long list stays usable. Persists the chosen vehicle to localStorage and shows
// an "Active vehicle" summary. Navigation target is the results collection
// filtered by the per-fitment tag: caryear:<brand>-<model>-<year>.

const STORAGE_KEY = "tapsy:active-vehicle";
// Fired whenever the saved vehicle changes, so every picker on the page stays in
// step — the header drawer and the inline picker on the collection page are two
// instances of this component and either can be the one that changed it.
const CHANGE_EVENT = "tapsy:vehicle-change";

function readActive() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}
function writeActive(v) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}
function clearActive() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function announceChange() {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

// The model list is the same for every picker, so fetch it once per page rather
// than once per instance.
let vehiclesRequest = null;
function fetchVehicles(url) {
  vehiclesRequest ??= fetch(url, { headers: { Accept: "application/json" } })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
  return vehiclesRequest;
}

// Match the dashboard's vehicle-tag slug (vehicle-tags.server.ts) so the
// caryear:<brand>-<model>-<year> navigation tags line up.
function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// A single searchable dropdown (combobox).
class Combo {
  constructor(root, placeholder, onChange) {
    this.root = root;
    this.placeholder = placeholder;
    this.onChange = onChange;
    this.trigger = root.querySelector("[data-vd-trigger]");
    this.valueEl = root.querySelector("[data-vd-value]");
    this.panel = root.querySelector("[data-vd-panel]");
    this.search = root.querySelector("[data-vd-search]");
    this.list = root.querySelector("[data-vd-list]");
    this.emptyEl = root.querySelector("[data-vd-empty]");
    this.options = [];
    this.value = "";
    this.label = "";

    this.trigger.addEventListener("click", () => this.toggle());
    this.search.addEventListener("input", () => this.render(this.search.value));
    this.search.addEventListener("keydown", (e) => this.onKey(e));
    // Close on any click that isn't inside this combo.
    document.addEventListener("click", (e) => {
      if (!this.root.contains(e.target)) this.close();
    });
  }

  get disabled() {
    return this.root.hasAttribute("data-disabled");
  }

  setDisabled(disabled) {
    if (disabled) {
      this.root.setAttribute("data-disabled", "");
      this.close();
    } else {
      this.root.removeAttribute("data-disabled");
    }
  }

  setOptions(options) {
    this.options = options || [];
  }

  toggle() {
    if (this.disabled) return;
    this.panel.hidden ? this.open() : this.close();
  }

  open() {
    if (this.disabled) return;
    this.panel.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.search.value = "";
    this.render("");
    requestAnimationFrame(() => this.search.focus());
  }

  close() {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
  }

  render(query) {
    const q = (query || "").trim().toLowerCase();
    const matches = q
      ? this.options.filter((o) => o.label.toLowerCase().includes(q))
      : this.options;
    const frag = document.createDocumentFragment();
    matches.forEach((o) => {
      const li = document.createElement("li");
      li.className = "vd-combo__option" + (o.value === this.value ? " is-selected" : "");
      li.setAttribute("role", "option");
      li.textContent = o.label;
      li.addEventListener("click", () => this.select(o));
      frag.appendChild(li);
    });
    this.list.replaceChildren(frag);
    if (this.emptyEl) this.emptyEl.hidden = matches.length > 0;
  }

  select(o) {
    this.value = o.value;
    this.label = o.label;
    this.valueEl.textContent = o.label;
    this.valueEl.classList.remove("is-placeholder");
    this.close();
    if (this.onChange) this.onChange(o);
  }

  reset() {
    this.value = "";
    this.label = "";
    this.options = [];
    this.valueEl.textContent = this.placeholder;
    this.valueEl.classList.add("is-placeholder");
    this.close();
  }

  onKey(e) {
    if (e.key === "Escape") {
      this.close();
      this.trigger.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const first = this.list.querySelector(".vd-combo__option");
      if (first) first.click();
    }
  }
}

class VehicleDrawer {
  constructor(root) {
    this.root = root;
    let data = { models: [], collection: "all" };
    try {
      data = JSON.parse(root.querySelector("[data-vd-data]").textContent);
    } catch (e) {
      console.warn("Vehicle drawer: could not parse data", e);
    }
    this.models = Array.isArray(data.models) ? data.models : [];
    this.collection = data.collection || root.dataset.collection || "all";
    this.baseUrl = root.dataset.baseUrl || "/collections/all";

    // Whether picking a vehicle should also jump to the results. Set on the
    // collection band; not on the header drawer, which must be able to set a
    // vehicle without navigating away from the current page.
    this.submitGoes = root.hasAttribute("data-vd-submit-goes");
    this.rootEl = root;

    this.selectBtn = root.querySelector("[data-vd-select]");
    this.selectorBox = root.querySelector("[data-vd-selector]");
    this.activeBox = root.querySelector("[data-vd-active]");
    this.activeName = root.querySelector("[data-vd-active-name]");
    this.fitBtn = root.querySelector("[data-vd-fit]");
    this.clearBtn = root.querySelector("[data-vd-clear]");

    this.brand = new Combo(root.querySelector('[data-vd-combo="brand"]'), "Make", (o) => this.onBrand(o));
    this.model = new Combo(root.querySelector('[data-vd-combo="model"]'), "Model", (o) => this.onModel(o));
    this.year = new Combo(root.querySelector('[data-vd-combo="year"]'), "Year", () => this.updateSelect());

    this.fillBrands();
    this.bind();
    this.renderActive();
    // Inline Liquid data (above) is capped at ~50 by Shopify; fetch the FULL
    // list from the Tapsy app proxy and replace it.
    this.loadFullList();
  }

  // Fetch every model from the app proxy (bypasses Liquid's ~50 cap).
  async loadFullList() {
    const url = this.root.dataset.vehiclesUrl;
    if (!url) return;
    try {
      const data = await fetchVehicles(url);
      if (!data || !Array.isArray(data.models) || data.models.length === 0) return;

      const now = new Date().getFullYear();
      this.models = data.models.map((m) => {
        let endYear = Number(m.endYear) || 0;
        if (!m.discontinued && endYear && endYear < now) endYear = now;
        return {
          brand: m.brand,
          brandSlug: slugify(m.brand),
          model: m.model,
          modelSlug: slugify(m.model),
          startYear: Number(m.startYear) || 0,
          endYear,
        };
      });
      // Refresh Make with the complete list (skip if a selection is in progress).
      if (!this.brand.value) this.fillBrands();
    } catch {
      /* keep the inline fallback */
    }
  }

  fillBrands() {
    const seen = new Map();
    this.models.forEach((m) => {
      if (m.brandSlug && !seen.has(m.brandSlug)) seen.set(m.brandSlug, m.brand);
    });
    const options = [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([slug, name]) => ({ value: slug, label: name }));
    this.brand.setOptions(options);
  }

  bind() {
    this.selectBtn.addEventListener("click", () => this.select());
    if (this.fitBtn) this.fitBtn.addEventListener("click", () => this.go(readActive()));
    if (this.clearBtn)
      this.clearBtn.addEventListener("click", () => {
        clearActive();
        this.resetSelector();
        this.renderActive();
        announceChange();
      });

    // Another picker (or another tab) changed the vehicle.
    document.addEventListener(CHANGE_EVENT, () => this.sync());
    window.addEventListener("storage", (event) => {
      if (event.key === STORAGE_KEY) this.sync();
    });
  }

  /** Bring this instance in line with whatever is currently saved. */
  sync() {
    // Cleared elsewhere: drop this instance's half-finished selection too,
    // otherwise it reopens showing a stale make/model.
    if (!readActive()) this.resetSelector();
    this.renderActive();
  }

  onBrand(o) {
    const models = this.models
      .filter((m) => m.brandSlug === o.value)
      .sort((a, b) => a.model.localeCompare(b.model))
      .map((m) => ({ value: m.modelSlug, label: m.model, startYear: m.startYear, endYear: m.endYear }));
    this.model.reset();
    this.model.setOptions(models);
    this.model.setDisabled(false);
    this.year.reset();
    this.year.setDisabled(true);
    this.updateSelect();
  }

  onModel(o) {
    const lo = parseInt(o.startYear, 10) || 0;
    const hi = parseInt(o.endYear, 10) || lo;
    const years = [];
    if (lo >= 1950) {
      for (let y = hi; y >= lo; y--) years.push({ value: String(y), label: String(y) });
    }
    this.year.reset();
    this.year.setOptions(years);
    this.year.setDisabled(years.length === 0);
    this.updateSelect();
  }

  updateSelect() {
    this.selectBtn.disabled = !(this.brand.value && this.model.value && this.year.value);
  }

  select() {
    if (this.selectBtn.disabled) return;
    writeActive({
      brand: this.brand.label,
      brandSlug: this.brand.value,
      model: this.model.label,
      modelSlug: this.model.value,
      year: this.year.value,
    });
    this.renderActive();
    announceChange();

    // On a surface whose whole job is finding keys — the collection band — the
    // pick IS the search, so a second "show keys that fit" click is a step that
    // asks the customer to confirm something they already said. Opt-in via
    // data-vd-submit-goes so the header drawer can still set a vehicle without
    // navigating away from whatever page you are on.
    if (this.submitGoes) this.go(readActive());
  }

  go(vehicle) {
    if (!vehicle) return;
    const tag = `caryear:${vehicle.brandSlug}-${vehicle.modelSlug}-${vehicle.year}`;
    const coll =
      this.collection && this.collection !== "all"
        ? `/collections/${this.collection}`
        : this.baseUrl;
    window.location.href = `${coll}?filter.p.tag=${encodeURIComponent(tag)}`;
  }

  resetSelector() {
    this.brand.reset();
    this.fillBrands();
    this.model.reset();
    this.model.setDisabled(true);
    this.year.reset();
    this.year.setDisabled(true);
    this.updateSelect();
  }

  /* Is the page actually showing results for the saved vehicle? Saving a car is
     local, but filtering is a URL parameter — so arriving at the collection from
     the nav gives you a band that says "Ford Ranger 2021" above the whole
     catalogue. The band has to be able to tell the difference. */
  syncFilterState(vehicle) {
    if (!this.rootEl) return;
    let applied = false;
    if (vehicle) {
      const tag = `caryear:${vehicle.brandSlug}-${vehicle.modelSlug}-${vehicle.year}`;
      applied = new URLSearchParams(window.location.search).getAll("filter.p.tag").includes(tag);
    }
    this.rootEl.toggleAttribute("data-vd-filtered", applied);
  }

  renderActive() {
    const v = readActive();
    this.syncFilterState(v);
    const has = Boolean(v && v.brand && v.model);
    const label = has ? `${v.brand} ${v.model}${v.year ? " " + v.year : ""}` : "Select Your Vehicle";

    if (this.activeBox) {
      if (has) this.activeName.textContent = label;
      this.activeBox.hidden = !has;
    }
    if (this.selectorBox) this.selectorBox.hidden = has;
    this.updateHeader(has ? label : null);
  }

  updateHeader(label) {
    document.querySelectorAll("[data-vehicle-trigger-label]").forEach((el) => {
      el.textContent = label || "Select Your Vehicle";
    });
    document.querySelectorAll("[data-vehicle-trigger]").forEach((el) => {
      el.classList.toggle("has-vehicle", Boolean(label));
    });
  }
}

document.querySelectorAll("[data-vehicle-drawer]").forEach((el) => new VehicleDrawer(el));
